import React, { useState, useRef, useEffect } from 'react'
import { Button, Input, Popover, message, Alert, Spin } from 'antd'
import { Plus, Link, AlertCircle, ArrowRight, Zap } from 'lucide-react'
import { useFFEStore } from '../../store/ffeStore'
import { useApi } from '../../services/ApiContext'
import { useCompanyStore } from '../../store/company/companyStore'
import { Link as RouterLink } from 'react-router-dom'

interface Props {
  groupId?: string
  isPrimary?: boolean
}

export const AddFromUrlButton: React.FC<Props> = ({ groupId, isPrimary = false }) => {
  const [url, setUrl] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [showProgress, setShowProgress] = useState(false)
  const plan = useCompanyStore((state) => state.company.plan)
  const user = useCompanyStore((state) => state.user)
  
  const inputRef = useRef<any>(null)
  const progressIntervalRef = useRef<number | null>(null)
  
  const addLineItem = useFFEStore((state) => state.addLineItem)
  const updateLineItem = useFFEStore((state) => state.updateLineItem)
  const removeLineItem = useFFEStore((state) => state.removeLineItem)
  const setLineItemLoading = useFFEStore((state) => state.setLineItemLoading)
  const suppliers = useFFEStore((state) => state.suppliers)
  const addSupplier = useFFEStore((state) => state.addSupplier)
  const defaultMargin = useFFEStore(
    (state) => state.document.settings.defaultMargin
  )
  
  const api = useApi()

  // Check if user is on free plan
  const isFreePlan = !plan || plan === 'free'

  const upgradeButtonRedirect = (user?: any) => {
    return user?.isDemo ? '/signup?redirect=/app/upgrade' : '/app/upgrade'
  }

  const isValidUrl = (urlString: string): boolean => {
    try {
      new URL(urlString)
      return true
    } catch {
      return false
    }
  }

  const handleAddFromUrl = async () => {
    if (!url.trim()) {
      setError('Please enter a valid URL')
      return
    }

    // Basic URL validation
    if (!isValidUrl(url)) {
      setError('Please enter a valid URL')
      return
    }

    setError(null)
    setShowProgress(false)

    let createdLineItemId: string | null = null

    try {
      // Add placeholder line item immediately and capture its id
      addLineItem(
        {
          name: 'Fetching product…',
          description: '',
          quantity: 1,
          margin: defaultMargin,
          costPrice: 0,
          sellPrice: 0,
          status: 'specified',
          groupId,
          productUrl: url
        },
        undefined,
        undefined,
        (id) => {
          createdLineItemId = id
          setLineItemLoading(id, true)
        }
      )

      // Close popover immediately for a snappier UX
      setIsPopoverOpen(false)
      setUrl('')

      // Fetch product details
      const lineItem = await api.addItemByUrl(url)

      // Match or create supplier by name, then set supplier to the supplier _id
      let supplierIdToSet: string | undefined
      const supplierName = (lineItem?.supplier || '').trim()
      if (supplierName) {
        const activeSuppliers = suppliers.filter((s) => !s.isArchived)
        const existing = activeSuppliers.find(
          (s) => s.name.toLowerCase() === supplierName.toLowerCase()
        )
        if (existing) {
          supplierIdToSet = existing._id
        } else {
          const newSupplier = { name: supplierName }
          const response = await api.createSupplier(newSupplier)
          addSupplier(response)
          supplierIdToSet = response._id
        }
      }

      if (createdLineItemId) {
        updateLineItem(createdLineItemId, {
          ...lineItem,
          supplier: supplierIdToSet,
          quantity: 1,
          margin: defaultMargin,
          status: 'specified',
          groupId
        })
        setLineItemLoading(createdLineItemId, false)
      }

      message.success('Product added successfully!')
    } catch (err) {
      console.error('Error fetching product:', err)
      if (createdLineItemId) {
        // Remove the placeholder if fetch fails
        removeLineItem(createdLineItemId)
      }
      setError('Failed to fetch product details. Please check the URL and try again.')
    }
  }


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setUrl(value)
    setError(null)
  }

  // Focus input when popover opens
  useEffect(() => {
    if (isPopoverOpen && inputRef.current) {
      // Small delay to ensure popover is fully rendered
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [isPopoverOpen])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
      }
    }
  }, [])

  // Upgrade prompt content for free plan users
  const upgradeContent = (
    <div className="w-80 p-4">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="h-5 w-5 text-primary" />
          <h4 className="text-sm font-medium text-gray-900">
            Add Products from URL
          </h4>
        </div>
        <p className="text-xs text-gray-600 mb-4">
          Automatically fetch product details including name, description, images, and pricing from any product URL. Save hours of manual data entry.
        </p>
      </div>
      
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-4 mb-4">
        <div className="space-y-2 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
            <span>Instant product data extraction</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
            <span>Automatic image and pricing import</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
            <span>Works with major retailers and suppliers</span>
          </div>
        </div>
      </div>
      
      <div className="flex gap-2">
        <RouterLink to={upgradeButtonRedirect(user)} className="flex-1">
          <Button type="primary" className="w-full">
            Upgrade to Unlock
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </RouterLink>
        <Button
          onClick={() => setIsPopoverOpen(false)}
        >
          Cancel
        </Button>
      </div>
    </div>
  )

  // Regular content for paid plan users
  const regularContent = (
    <div className="w-80 p-4">
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">
          Add Product from URL
        </h4>
        <p className="text-xs text-gray-600 mb-4">
          Enter a product URL to automatically fetch product details including name, description, images, and pricing.
        </p>
      </div>
      
      <div className="space-y-3">
        <Input
          ref={inputRef}
          placeholder="https://example.com/product/123"
          value={url}
          onChange={handleInputChange}
          onPressEnter={handleAddFromUrl}
          prefix={<Link className="h-3 w-3 text-gray-400" />}
        />
        <Spin spinning={showProgress}/>
        
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            icon={<AlertCircle className="h-3 w-3" />}
            className="text-xs"
          />
        )}
        
        <div className="flex gap-2">
          <Button
            type="primary"
            onClick={handleAddFromUrl}
            disabled={!url.trim() || showProgress}
            className="flex-1"
          >
            Add Product
          </Button>
          <Button
            onClick={() => {
              setIsPopoverOpen(false)
              setUrl('')
              setError(null)
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )

  return (
    <Popover 
      content={isFreePlan ? upgradeContent : regularContent}
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
      trigger="click"
      placement="bottomLeft"
    >
      <Button
        type={isPrimary ? 'primary' : 'default'}
        icon={<Plus className="h-4 w-4" />}
      >
        Add from URL
      </Button>
    </Popover>
  )
} 